<template>
	<v-container
		v-if="mode === Mode.TABLE_CELL || mode === Mode.BLANK_TABLE_CELL"
		class="footer-row d-flex align-center flex-nowrap mt-2 py-0 px-2"
	>
		<div class="footer-item-container flex-grow-1 align-center">
			<v-checkbox
				:model-value="isBlankTableCell"
				:disabled="readOnly"
				label="Blank table cell"
				class="mt-0"
				density="compact"
				hide-details
				@update:model-value="updateBlankTableCell"
			/>
		</div>
		<div
			v-if="headerData && mode !== Mode.BLANK_TABLE_CELL"
			class="d-flex flex-grow-0 justify-end flex-nowrap align-center"
		>
			<span class="table-header-data-button-label mr-2">
				View imported header tags
			</span>
			<v-btn icon size="x-small" @click="toggleHeaderData">
				<v-icon
					:class="{
						'table-cell-header-data-button': true,
						'rotate-180': isHeaderDataOpen,
					}"
				>
					mdi-chevron-down
				</v-icon>
			</v-btn>
		</div>
	</v-container>
	<v-container
		v-if="mode === Mode.TABLE_CELL && isHeaderDataOpen"
		class="mt-1 py-0 px-2"
	>
		<v-metadata-table-header-summary
			v-if="isHeaderDataOpen && headerData && mode !== Mode.BLANK_TABLE_CELL"
			axis-label="Row data"
			:header-data="filteredRowHeaderData"
		/>
		<v-divider
			v-if="isHeaderDataOpen && hasBothHeaderData"
			class="my-1 mx-2"
		></v-divider>
		<v-metadata-table-header-summary
			v-if="isHeaderDataOpen && headerData && mode !== Mode.BLANK_TABLE_CELL"
			axis-label="Column data"
			:header-data="filteredColHeaderData"
		/>
	</v-container>
	<v-container
		v-if="axisType === 'tableHeader' && !hasInputMetadata()"
		class="table-header-warning-container"
	>
		Warning:
		<ul class="ml-5">
			<li>
				Leaving row and column table headers blank will result in blank table
				cells
			</li>
			<li>
				Each table cell that contains data must reference
				<i>exactly</i> one study branch in either its row or column header
			</li>
		</ul>
	</v-container>
</template>

<script>
import _ from "lodash";
import { repeatTagWhitelist } from "@/constants";

import VMetadataTableHeaderSummary from "./VMetadataTableHeaderSummary.vue";
import { Mode } from "./VMetadataConstants.js";

export default {
	name: "VMetadataTableFooter",

	components: {
		VMetadataTableHeaderSummary,
	},

	inject: ["validateValues"],

	props: {
		axisType: { type: String, default: null },
		compositeKeyUtils: { type: Object, default: null },
		currentTagGroupTab: { type: Number, default: 0 },
		headerData: { type: Object, default: null },
		isBlankTableCell: { type: Boolean, default: false },
		metadata: { type: Array, default: () => [] },
		mode: { type: String, default: Mode.STANDARD },
		readOnly: { type: Boolean, default: false },
		tagGroupTabs: { type: Array, default: () => [] },
	},

	emits: ["update:isBlankTableCell", "update:mode"],

	setup() {},

	data() {
		return {
			isHeaderDataOpen: false,
			Mode,
		};
	},
	computed: {
		filteredRowHeaderData() {
			return this.filterHeaderDataToCurrentTagGroup(
				this.headerData?.rowHeaderData || [],
				this.currentTagGroupTab,
			);
		},
		filteredColHeaderData() {
			return this.filterHeaderDataToCurrentTagGroup(
				this.headerData?.colHeaderData || [],
				this.currentTagGroupTab,
			);
		},
		hasBothHeaderData() {
			return (
				this.filteredRowHeaderData.length > 0 &&
				this.filteredColHeaderData.length > 0
			);
		},
	},

	methods: {
		filterHeaderDataToCurrentTagGroup(headerData, currentTagGroupTab) {
			if (_.isEmpty(this.tagGroupTabs)) {
				return headerData;
			}

			const activeTab = this.tagGroupTabs[currentTagGroupTab] || null;

			const cellTags = new Set(
				Object.keys(this.metadata)
					.filter(
						(key) =>
							!isNaN(key) &&
							!this.metadata[key].headerTag &&
							this.metadata[key].tag &&
							!_.isNil(this.metadata[key].value) &&
							(this.tagGroupTabs?.length < 2 ||
								this.metadata[key].tagGroup?.uuid === activeTab?.uuid),
					)
					.map((key) => this.metadata[key].tag.uuid),
			);

			const whitelistTags = repeatTagWhitelist;
			const filteredHeaderData = _.cloneDeep(headerData)
				.filter((data) => {
					return (
						(activeTab &&
							data.tagGroup &&
							data.tagGroup.uuid === activeTab.uuid) ||
						this.compositeKeyUtils.isBranchTag(data.tag.name)
					);
				})
				.map((data) => {
					if (
						cellTags.has(data.tag.uuid) &&
						!whitelistTags.includes(data.tag.name)
					) {
						data.overridden = true;
					} else {
						data.overridden = false;
					}

					if (this.compositeKeyUtils.isReferenceBranchTag(data.tag.name)) {
						data.value = this.compositeKeyUtils.getCompositeKeyValue(
							data.value,
						);
					}
					return data;
				});

			return filteredHeaderData;
		},
		hasInputMetadata: function () {
			return this.metadata.find((metadatum) => {
				return !isNaN(metadatum.index) && !_.isEmpty(metadatum.value);
			});
		},
		updateBlankTableCell(value) {
			this.validateValues(value);
			this.$emit("update:isBlankTableCell", value);
			this.$emit(
				"update:mode",
				value ? Mode.BLANK_TABLE_CELL : Mode.TABLE_CELL,
			);
		},
		toggleHeaderData() {
			this.isHeaderDataOpen = !this.isHeaderDataOpen;
		},
	},
};
</script>

<style lang="scss">
.footer-row {
	height: 40px;
}

.footer-row .v-label {
	font-size: 14px;
}

.footer-item-container {
	height: 40px;
	flex-basis: 0;
}

.rotate-180 {
	transform: rotate(180deg);
}

.table-cell-header-data-button {
	transition: transform 0.3s ease;
}

.table-header-data-button-label {
	font-size: 14px;
}

.table-header-warning-container {
	font-size: 12px;
}
</style>
